import { show } from "@ebay/nice-modal-react";
import { createAsyncThunk } from "@reduxjs/toolkit";

import { client } from "@/client/apollo";
import { __GLOBAL_SESSION_ID } from "@/client/globals";
import { RootState } from "@/data/store";
import {
  SentimentCheckInput,
  SubmitSentimentCheckDocument,
  SubmitSentimentCheckMutation,
  SubmitSentimentCheckMutationVariables,
} from "@/generated/graphql";

import { SentimentCheckModal } from "./sentiment-check-modal";
import { endSentimentCheck, nextSentimentCheck } from "./sentiment-check-slice";
import { findNextSchedule } from "./utils";

/**
 * Show the sentiment check modal.
 */
export const showSentimentCheckModal = createAsyncThunk(
  "sentimentCheck/showModal",
  async (payload: { assessmentId: string }, { dispatch, getState }) => {
    const { assessmentId } = payload;

    const sessionId = __GLOBAL_SESSION_ID.current;
    if (!sessionId) {
      throw new Error("Session ID is not set");
    }
    const state = getState() as RootState;
    const prevSessionId = state.sentimentCheck.prevSessionId;
    if (prevSessionId === sessionId) return;
    show(SentimentCheckModal, {
      id: "sentiment-check-modal",
      onSubmit: async (input: SentimentCheckInput) => {
        await dispatch(
          submitSentimentCheck({
            assessmentId,
            input,
          })
        );
      },
    });
  }
);

/**
 * Create a sentiment check.
 * After submission, advance to the next scheduled check date.
 */
export const submitSentimentCheck = createAsyncThunk(
  "sentimentCheck/submit",
  async (
    payload: SubmitSentimentCheckMutationVariables,
    { dispatch, getState }
  ) => {
    const { assessmentId, input } = payload;
    const { data } = await client.mutate<
      SubmitSentimentCheckMutation,
      SubmitSentimentCheckMutationVariables
    >({
      mutation: SubmitSentimentCheckDocument,
      variables: { assessmentId, input },
    });

    if (data?.submitSentimentCheck) {
      const state = getState() as RootState;
      const { schedules } = state.sentimentCheck;

      // The check just submitted — use current time as the lastCheckDate
      const nextDate = findNextSchedule(schedules, new Date().toISOString());
      if (nextDate) {
        dispatch(nextSentimentCheck({ date: nextDate }));
      } else {
        dispatch(endSentimentCheck());
      }
    }

    return data?.submitSentimentCheck;
  }
);
